
/**
 * Utility functions for optimizing images with enhanced mobile optimization
 */

/**
 * Compresses an image file and returns a data URL with adaptive compression based on device capabilities
 * 
 * @param file - The image file to compress
 * @param maxWidth - Maximum width of the compressed image
 * @param quality - Image quality (0-1)
 * @returns Promise with compressed image data URL
 */
export async function compressImage(
  file: File, 
  maxWidth = 1200, 
  quality = 0.8
): Promise<string> {
  // Detect if we're on a mobile device or low-end device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isLowEndDevice = (navigator as any).deviceMemory && (navigator as any).deviceMemory < 4;
  
  // Adjust compression parameters based on device capabilities
  if (isMobile || isLowEndDevice) {
    maxWidth = Math.min(maxWidth, 800); // Smaller images for mobile
    quality = Math.min(quality, 0.7); // Higher compression for mobile
  }
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        // Create canvas with proper dimensions
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image to canvas with new dimensions
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Use better quality settings for photos vs. graphics
        if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
          // For photos (JPEGs), use bilinear interpolation for smoother results
          ctx.imageSmoothingQuality = 'high';
        } else {
          // For graphics (PNG, SVG), use nearest neighbor for sharper edges
          ctx.imageSmoothingEnabled = false;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed data URL - use webp if supported
        try {
          // Try WebP for better compression if supported
          if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
            const webpDataUrl = canvas.toDataURL('image/webp', quality);
            resolve(webpDataUrl);
          } else {
            // Fall back to JPEG/PNG
            const compressedDataUrl = canvas.toDataURL(
              file.type === 'image/png' ? 'image/png' : 'image/jpeg', 
              quality
            );
            resolve(compressedDataUrl);
          }
        } catch (err) {
          // If WebP fails, use original format
          const compressedDataUrl = canvas.toDataURL(file.type, quality);
          resolve(compressedDataUrl);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Error loading image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
  });
}

/**
 * Optimize an image before uploading with improved mobile-first approach
 * 
 * @param file - The image file to optimize
 * @returns Promise with optimized image data
 */
export async function optimizeImageBeforeUpload(file: File): Promise<string> {
  // Check if file is an image
  if (!file.type.startsWith('image/')) {
    throw new Error('File is not an image');
  }
  
  // Detect network conditions to adjust compression level
  const connection = (navigator as any).connection;
  const isSlowConnection = connection && 
    (connection.effectiveType === 'slow-2g' || 
     connection.effectiveType === '2g' || 
     connection.downlink < 1);
  
  // Calculate appropriate max width based on file size, device and network
  const fileSizeInMB = file.size / (1024 * 1024);
  let maxWidth = 800; // More aggressive default max width for mobile
  let quality = 0.75;  // Slightly lower default quality for better compression
  
  if (isSlowConnection || fileSizeInMB > 5) {
    maxWidth = 600;
    quality = 0.6;
  } else if (fileSizeInMB > 2) {
    maxWidth = 800;
    quality = 0.7;
  }
  
  // Compress the image
  return compressImage(file, maxWidth, quality);
}

/**
 * Generate a low-quality image placeholder for immediate display
 * 
 * @param file - The image file to generate a placeholder for
 * @returns Promise with placeholder image data URL
 */
export async function generateImagePlaceholder(file: File): Promise<string> {
  // Tiny placeholder for immediate display
  return compressImage(file, 20, 0.1);
}

/**
 * Get responsive image sizes based on device capabilities
 * 
 * @returns Object with optimal image dimensions for current device
 */
export function getResponsiveImageSizes(): { width: number, quality: number } {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isLowEndDevice = (navigator as any).deviceMemory && (navigator as any).deviceMemory < 4;
  const connection = (navigator as any).connection;
  const isSlowConnection = connection && 
    (connection.effectiveType === 'slow-2g' || 
     connection.effectiveType === '2g' || 
     connection.downlink < 1);
  
  // Default for desktop
  let width = 1200;
  let quality = 0.8;
  
  // Adjust for mobile
  if (isMobile) {
    width = 800;
    quality = 0.75;
  }
  
  // Further adjust for low-end devices or slow connections
  if (isLowEndDevice || isSlowConnection) {
    width = 600;
    quality = 0.6;
  }
  
  return { width, quality };
}
